import { FormControl, FormLabel, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

type CreatableAutocompleteProps<T> = {
  options: T[];
  value: T | null;
  title: string;
  onChange: (value: T | null) => void;
  onCreate: (label: string) => Promise<T>; // called when user types a new value + Enter
  getLabel: (item: T) => string;
  getId: (item: T) => string;
  placeholder?: string;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  size?: "small" | "medium";
  sx?: any;
};

export function CreatableAutocomplete<T>({
  options,
  value,
  title,
  onChange,
  onCreate,
  getLabel,
  getId,
  placeholder = "Type and press Enter…",
  loading,
  error,
  helperText,
  size = "medium",
  sx,
}: CreatableAutocompleteProps<T>) {
  const handleChange = async (_: any, newValue: T | string | null) => {
    if (typeof newValue === "string") {
      const typed = newValue.trim();
      if (!typed) return;

      // if it already exists (case-insensitive), just select it
      const existing = options.find(
        (o) => getLabel(o).toLowerCase() === typed.toLowerCase()
      );
      if (existing) {
        onChange(existing);
      } else {
        const created = await onCreate(typed);
        onChange(created);
      }
      return;
    }

    onChange(newValue as T | null);
  };

  return (
    <FormControl>
      <FormLabel htmlFor="title">{title}</FormLabel>
      <Autocomplete
        freeSolo
        options={options}
        value={value}
        loading={loading}
        isOptionEqualToValue={(o, v) => getId(o) === getId(v)}
        getOptionLabel={(opt) =>
          typeof opt === "string" ? opt : getLabel(opt)
        }
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={!!error}
            helperText={helperText}
            size={size}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: size === "small" ? 40 : 50,
              },
            }}
          />
        )}
        sx={sx}
      />
    </FormControl>
  );
}
